import { auth } from "../components/config/firebase";

/**
 * ========================================================================
 * FIREBASE TOKEN MANAGEMENT SYSTEM
 * ========================================================================
 * 
 * Firebase ID tokens expire after 1 hour. This module handles:
 * 
 * 1. AUTOMATIC TOKEN REFRESH
 *    - Tokens are automatically refreshed every 55 minutes (before expiry)
 *    - Background timer runs continuously while user is logged in
 * 
 * 2. ON-DEMAND REFRESH
 *    - getFirebaseToken() always returns a valid token
 *    - If token is expired, it's automatically refreshed
 *    - Use refreshFirebaseToken() to force immediate refresh
 * 
 * 3. ERROR RECOVERY
 *    - 401 errors trigger automatic token refresh (use fetchWithTokenRefresh)
 *    - Failed API calls are retried with fresh token
 * 
 * 4. LIFECYCLE MANAGEMENT
 *    - Token refresh starts automatically on login
 *    - Stops automatically on logout
 * 
 * USAGE:
 * ------
 * // Get token for API calls (always fresh)
 * const token = await getFirebaseToken();
 * 
 * // Force refresh (on 401 errors)
 * const freshToken = await refreshFirebaseToken();
 * 
 * // Logout (stops token refresh)
 * await logout();
 * 
 * ========================================================================
 */

// Token refresh configuration
const TOKEN_EXPIRY_TIME = 55 * 60 * 1000; // Firebase tokens expire in 1 hour, refresh at 55 mins
let tokenRefreshTimer = null;

/**
 * Get the current Firebase ID token for API authentication
 * Always returns a fresh token, automatically refreshing if needed
 * @param {boolean} forceRefresh - Force refresh the token regardless of expiry
 * @returns {Promise<string>} Firebase ID token or empty string if not authenticated
 */
export async function getFirebaseToken(forceRefresh = false) {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.warn('No authenticated user found');
      return "";
    }
    
    // Force refresh if requested, or get token (auto-refreshes if expired)
    // forceRefresh=true ensures we get a fresh token even if current one is valid
    const token = await user.getIdToken(forceRefresh);
    
    // Setup automatic refresh in background if not already running
    if (!tokenRefreshTimer) {
      setupTokenRefresh();
    }
    
    return token;
  } catch (error) {
    console.error('Error getting Firebase token:', error);
    return "";
  }
}

/**
 * Get Firebase ID token and return it (alias for clarity)
 * This is useful when you need to send the token to backend verification endpoints
 * @returns {Promise<string>} Firebase ID token
 */
export async function getFirebaseIdToken() {
  return await getFirebaseToken();
}

/**
 * Force refresh the Firebase token
 * Use this when you receive 401 errors or need a guaranteed fresh token
 * @returns {Promise<string>} Fresh Firebase ID token
 */
export async function refreshFirebaseToken() {
  console.log('Force refreshing Firebase token...');
  return await getFirebaseToken(true);
}

/**
 * Setup automatic token refresh in background
 * Refreshes token every 55 minutes (before 1-hour expiry)
 */
function setupTokenRefresh() {
  // Clear any existing timer
  if (tokenRefreshTimer) {
    clearInterval(tokenRefreshTimer);
  }
  
  // Refresh token every 55 minutes
  tokenRefreshTimer = setInterval(async () => {
    const user = auth.currentUser;
    if (user) {
      try {
        console.log('Background token refresh triggered...');
        await user.getIdToken(true); // Force refresh
        console.log('Token refreshed successfully');
      } catch (error) {
        console.error('Background token refresh failed:', error);
      }
    } else {
      // User logged out, stop refresh timer
      clearInterval(tokenRefreshTimer);
      tokenRefreshTimer = null;
    }
  }, TOKEN_EXPIRY_TIME);
  
  console.log('Token auto-refresh enabled (every 55 minutes)');
}

/**
 * Stop automatic token refresh
 * Call this on logout
 */
export function stopTokenRefresh() {
  if (tokenRefreshTimer) {
    clearInterval(tokenRefreshTimer);
    tokenRefreshTimer = null;
    console.log('Token auto-refresh disabled');
  }
}

/**
 * Get Firebase token synchronously from cache (for cases where async isn't possible)
 * Note: This may return an expired token. Use getFirebaseToken() when possible.
 * @returns {string} Cached Firebase ID token or empty string
 */
export function getFirebaseTokenSync() {
  try {
    const user = auth.currentUser;
    if (!user) {
      return "";
    }
    // Access the cached token (may be expired)
    return user.accessToken || user.stsTokenManager?.accessToken || "";
  } catch (error) {
    console.error('Error getting Firebase token sync:', error);
    return "";
  }
}

/**
 * Check if user is authenticated
 * @returns {boolean} True if user is authenticated
 */
export function isAuthenticated() {
  return !!auth.currentUser;
}

/**
 * Get the current Firebase user
 * @returns {Object|null} Current Firebase user or null
 */
export function getCurrentUser() {
  return auth.currentUser;
}

/**
 * Wait for Firebase auth to initialize
 * @returns {Promise<Object|null>} Current user or null
 */
export function waitForAuthInit() {
  return new Promise((resolve) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    });
  });
}
